import { ChainId, tokens } from '@endure/chains';
import BigNumber from 'bignumber.js';

import { addressMappingAbi, stakingV2Abi } from 'libs/contracts/abis/bittensorPrecompiles';
import { wAlphaAbi } from 'libs/contracts/abis/bittensorWrappers';
import {
  ADDRESS_MAPPING_ADDRESS,
  STAKING_V2_ADDRESS,
} from 'libs/contracts/config/bittensorPrecompiles';
import { WALPHA_FALLBACK_TOKENS } from 'libs/contracts/config/bittensorTestnetFaucets';
import type { Token } from 'types';
import type { Address, Hex, PublicClient } from 'viem';

type MulticallResult<T> =
  | {
      status: 'success';
      result: T;
    }
  | {
      status: 'failure';
    };

type WAlphaMetadata = {
  netuid: bigint;
  hotkey: Hex;
  exchangeRate: bigint;
};

export interface BittensorNativeAlphaBalance {
  wrapperToken: Token;
  wrapperAddress: Address;
  netuid: bigint;
  hotkey: Hex;
  exchangeRate: bigint;
  balanceMantissa: BigNumber;
  stakingAllowanceMantissa: BigNumber;
}

export interface GetBittensorNativeBalancesInput {
  publicClient: PublicClient;
  accountAddress: Address;
  chainId: ChainId;
}

export interface GetBittensorNativeBalancesOutput {
  nativeTaoBalanceMantissa: BigNumber;
  coldkey?: Hex;
  alphaBalances: BittensorNativeAlphaBalance[];
}

const getDeployedWAlphaTokens = () => {
  const chainTokens = tokens[ChainId.BITTENSOR_TESTNET];

  return WALPHA_FALLBACK_TOKENS.map(
    (fallbackToken) =>
      chainTokens.find(
        (token) => token.address.toLowerCase() === fallbackToken.address.toLowerCase(),
      ) ?? fallbackToken,
  );
};

const unwrapMulticallResult = <T>(result: MulticallResult<T>) => {
  if (result.status === 'failure') {
    throw new Error('Failed to fetch Bittensor native balance');
  }

  return result.result;
};

export const getBittensorNativeBalances = async ({
  publicClient,
  accountAddress,
  chainId,
}: GetBittensorNativeBalancesInput): Promise<GetBittensorNativeBalancesOutput> => {
  if (chainId !== ChainId.BITTENSOR_TESTNET) {
    return {
      nativeTaoBalanceMantissa: new BigNumber(0),
      alphaBalances: [],
    };
  }

  const [nativeTaoBalance, coldkey] = await Promise.all([
    publicClient.getBalance({
      address: accountAddress,
    }),
    publicClient.readContract({
      address: ADDRESS_MAPPING_ADDRESS,
      abi: addressMappingAbi,
      functionName: 'addressMapping',
      args: [accountAddress],
    }),
  ]);

  const wrapperTokens = getDeployedWAlphaTokens();
  const wrapperMetadataResults = (await publicClient.multicall({
    contracts: wrapperTokens.flatMap((wrapperToken) => [
      {
        address: wrapperToken.address,
        abi: wAlphaAbi,
        functionName: 'netuid',
      },
      {
        address: wrapperToken.address,
        abi: wAlphaAbi,
        functionName: 'hotkey',
      },
      {
        address: wrapperToken.address,
        abi: wAlphaAbi,
        functionName: 'exchangeRate',
      },
    ]) as never,
  })) as unknown as MulticallResult<bigint | Hex>[];

  const wrapperMetadata = wrapperTokens.map<WAlphaMetadata>((_, index) => ({
    netuid: unwrapMulticallResult(wrapperMetadataResults[index * 3]!) as bigint,
    hotkey: unwrapMulticallResult(wrapperMetadataResults[index * 3 + 1]!) as Hex,
    exchangeRate: unwrapMulticallResult(wrapperMetadataResults[index * 3 + 2]!) as bigint,
  }));

  const alphaBalanceResults = (await publicClient.multicall({
    contracts: wrapperTokens.flatMap((wrapperToken, index) => {
      const { hotkey, netuid } = wrapperMetadata[index]!;

      return [
        {
          address: STAKING_V2_ADDRESS,
          abi: stakingV2Abi,
          functionName: 'getStake',
          args: [hotkey, coldkey, netuid],
        },
        {
          address: STAKING_V2_ADDRESS,
          abi: stakingV2Abi,
          functionName: 'allowance',
          args: [accountAddress, wrapperToken.address, netuid],
        },
      ];
    }) as never,
  })) as unknown as MulticallResult<bigint>[];

  const alphaBalances = wrapperTokens.map<BittensorNativeAlphaBalance>((wrapperToken, index) => {
    const { netuid, hotkey, exchangeRate } = wrapperMetadata[index]!;
    const balance = unwrapMulticallResult(alphaBalanceResults[index * 2]!);
    const stakingAllowance = unwrapMulticallResult(alphaBalanceResults[index * 2 + 1]!);

    return {
      wrapperToken,
      wrapperAddress: wrapperToken.address,
      netuid,
      hotkey,
      exchangeRate,
      balanceMantissa: new BigNumber(balance.toString()),
      stakingAllowanceMantissa: new BigNumber(stakingAllowance.toString()),
    };
  });

  return {
    nativeTaoBalanceMantissa: new BigNumber(nativeTaoBalance.toString()),
    coldkey,
    alphaBalances,
  };
};
